/**
 * Sysadmin Tenant Detail — Settings tab (R5 §4.2 editable fields).
 *
 * Spec §4.2 [v1]: name / slug / mode / region / timezone /
 * primary contact. Slug + mode remain read-only — slug renames trigger
 * a Qdrant collection rename job that lives in a dedicated workflow,
 * and mode is a deploy-time invariant on master.
 *
 * Editable in v1:
 *   - name        (string)
 *   - region      (curated dropdown, free-form column)
 *   - timezone    (Intl.supportedValuesOf('timeZone') curated list)
 *   - primary contact email (resolved server-side → user_id, must be
 *     a member of this tenant)
 *
 * Sticky save bar appears once a field is dirty. Reset reverts to
 * the loaded snapshot. Pre-save validation: email format + timezone
 * known to runtime.
 */

import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { Save, RotateCcw, Mail, MapPin, Globe, AlertCircle, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { SectionCard } from '@/components/ui/section-card';
import { SettingField } from '@/components/ui/setting-field';
import { useTenantDetail, useUpdateTenantSettings } from '@/lib/use-tenants';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/tenants/$tenantId/settings')({
  component: TenantSettingsTab,
});

/**
 * Curated region dropdown. The column is free-form so future regions
 * don't require a migration; this list is what the UI offers.
 */
const REGIONS: { value: string; label: string }[] = [
  { value: 'us-east', label: 'US East (N. Virginia)' },
  { value: 'us-west', label: 'US West (Oregon)' },
  { value: 'us-central', label: 'US Central (Iowa)' },
  { value: 'eu-central', label: 'EU Central (Frankfurt)' },
  { value: 'eu-west', label: 'EU West (Dublin)' },
  { value: 'ap-southeast', label: 'Asia Pacific (Singapore)' },
  { value: 'ap-northeast', label: 'Asia Pacific (Tokyo)' },
  { value: 'on-prem', label: 'On-premise (air-gap)' },
];

/**
 * Top-N timezone shortcuts surfaced as a separate Select group, plus
 * a free-text fallback that accepts any IANA tz. Intl.supportedValuesOf
 * returns ~400 entries — the curated head covers >95% of customers
 * without a 400-row dropdown.
 */
const TIMEZONE_SHORTCUTS = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Paris',
  'Europe/Madrid',
  'Europe/Athens',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
];

// NOTE (Foundations 1 / B0-T1): the Plan selector is gone. The founder
// abolished plans on 2026-09-08 (decisions.md, "Features, flags and plans" 2):
// deployments are on-prem, one customer per deployment.

function TenantSettingsTab() {
  const { tenantId } = useParams({ from: '/sysadmin/tenants/$tenantId/settings' });
  const { data } = useTenantDetail(tenantId);
  const update = useUpdateTenantSettings();

  // Form state mirrors the server snapshot once loaded; dirty-detection
  // is field-level (string comparison vs the snapshot).
  const [name, setName] = useState('');
  const [region, setRegion] = useState<string>('');
  const [timezone, setTimezone] = useState<string>('');
  const [primaryContactEmail, setPrimaryContactEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string | null>(null);

  // Hydrate form state on data load. Reset when tenantId changes.
  useEffect(() => {
    if (!data) return;
    setName(data.tenant.name);
    setRegion(data.tenant.region ?? '');
    setTimezone(data.tenant.timezone ?? '');
    setPrimaryContactEmail(data.tenant.primaryContactEmail ?? '');
    setEmailError(null);
  }, [data, tenantId]);

  const dirty = useMemo(() => {
    if (!data) return false;
    return (
      name !== data.tenant.name ||
      region !== (data.tenant.region ?? '') ||
      timezone !== (data.tenant.timezone ?? '') ||
      primaryContactEmail !== (data.tenant.primaryContactEmail ?? '')
    );
  }, [data, name, region, timezone, primaryContactEmail]);

  function reset() {
    if (!data) return;
    setName(data.tenant.name);
    setRegion(data.tenant.region ?? '');
    setTimezone(data.tenant.timezone ?? '');
    setPrimaryContactEmail(data.tenant.primaryContactEmail ?? '');
    setEmailError(null);
  }

  function validateEmail(): boolean {
    if (!primaryContactEmail) {
      setEmailError(null);
      return true;
    }
    // Cheap RFC-5322-ish validator; server is authoritative.
    const ok = /^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(primaryContactEmail);
    setEmailError(ok ? null : 'Enter a valid email address.');
    return ok;
  }

  async function save() {
    if (!data) return;
    if (!validateEmail()) return;

    // Build the patch body from only-dirty keys so the audit diff
    // reflects user intent (no spurious updates to unchanged fields).
    const body: Record<string, unknown> = {};
    if (name !== data.tenant.name) body.name = name;
    if (region !== (data.tenant.region ?? ''))
      body.region = region.trim() === '' ? null : region;
    if (timezone !== (data.tenant.timezone ?? ''))
      body.timezone = timezone.trim() === '' ? null : timezone;
    if (primaryContactEmail !== (data.tenant.primaryContactEmail ?? ''))
      body.primaryContactEmail =
        primaryContactEmail.trim() === '' ? null : primaryContactEmail;

    try {
      await update.mutateAsync({ id: tenantId, body });
      toast.success('Settings saved');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      // Map known server errors to inline feedback.
      if (msg.includes('CONTACT_NOT_IN_TENANT')) {
        setEmailError('No user with that email belongs to this tenant.');
      } else if (msg.includes('INVALID_TIMEZONE')) {
        toast.error('Unknown timezone', {
          description: 'Pick a value from the dropdown or enter an IANA timezone (e.g. America/Los_Angeles).',
        });
      } else {
        toast.error('Save failed', { description: msg });
      }
    }
  }

  if (!data) return null;
  const { tenant } = data;

  return (
    <div className="space-y-5">
      {/*
        Issue #956: every tenant detail tab must render its own h1 so
        the a11y landmark + page selector survive direct reload. The
        parent layout h1 is "{tenant.name}" — the tab-level h1 here
        names the section the operator is on.
      */}
      <h1 className="text-xl font-semibold tracking-tight">Settings</h1>
      <SectionCard
        title="Identity"
        icon={Globe}
        description="Tenant-facing identifiers. Slug and mode are deploy-time invariants; renames live in a dedicated workflow."
      >
        <SettingField
          label="Name"
          description="Display name shown in the tenant header and audit log."
        >
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={255}
            placeholder="Tenant display name"
          />
        </SettingField>

        <SettingField
          label="Slug"
          readOnly
          readOnlyReason="Slug renames trigger a Qdrant collection rename job and run in a dedicated workflow."
          description="URL-affecting identifier."
        >
          <Input value={tenant.slug} readOnly className="font-mono text-xs bg-muted/50" />
        </SettingField>

        <SettingField
          label="Code"
          readOnly
          description="Human-readable org code for tenant switching."
        >
          <Input value={tenant.orgCode} readOnly className="font-mono text-xs bg-muted/50" />
        </SettingField>

        <SettingField
          label="Mode"
          readOnly
          readOnlyReason="Deployment mode is a per-tenant invariant set at provisioning time."
        >
          <Input value={tenant.mode} readOnly className="bg-muted/50" />
        </SettingField>

        {tenant.isRoot && (
          <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2">
            <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <div>
              Root tenant. Cannot be suspended or deleted; rename
              operations require platform-level approval.
            </div>
          </div>
        )}
      </SectionCard>

      <SectionCard
        title="Location"
        icon={MapPin}
        description="Deployment region and operational timezone."
      >
        <SettingField label="Region" description="Geographic deployment label. Free-form column; curated dropdown for v1.">
          <Select value={region || '__none'} onValueChange={(v) => setRegion(v === '__none' ? '' : v)}>
            <SelectTrigger>
              <SelectValue placeholder="(not set)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">(not set — inherit platform default)</SelectItem>
              {REGIONS.map((r) => (
                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SettingField>

        <SettingField
          label="Timezone"
          description="IANA timezone string. The runtime computes tenant-local times via Intl.DateTimeFormat."
        >
          <Select
            value={timezone || '__none'}
            onValueChange={(v) => setTimezone(v === '__none' ? '' : v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="(not set)" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="__none">(not set — inherit platform default UTC)</SelectItem>
              {TIMEZONE_SHORTCUTS.map((tz) => (
                <SelectItem key={tz} value={tz} className="font-mono text-xs">
                  {tz}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SettingField>
      </SectionCard>

      <SectionCard
        title="Primary contact"
        icon={Mail}
        description="Operational point-of-contact for this tenant. Must be a member of this tenant."
      >
        <SettingField
          label="Email"
          description="Server resolves email → user; rejects emails that don't belong to this tenant."
        >
          <div className="space-y-1">
            <Input
              type="email"
              value={primaryContactEmail}
              onChange={(e) => {
                setPrimaryContactEmail(e.target.value);
                if (emailError) setEmailError(null);
              }}
              onBlur={validateEmail}
              placeholder="ops-lead@tenant.example"
              className={cn(emailError && 'border-destructive focus-visible:ring-destructive')}
            />
            {emailError && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {emailError}
              </p>
            )}
            {tenant.primaryContactName && !emailError && primaryContactEmail === tenant.primaryContactEmail && (
              <p className="text-xs text-muted-foreground">
                Resolves to {tenant.primaryContactName}.
              </p>
            )}
          </div>
        </SettingField>
      </SectionCard>

      {/*
        Foundations 1 / B0-T3 (SEC-6) — the two tenant policy switches,
        READ-ONLY.

        The sysadmin chooses their INITIAL values in the provisioning wizard
        and never touches them again: after creation they belong to the tenant
        admin (decisions.md 2026-09-08, "two tenant admin settings, owned by
        the tenant admin — not the sysadmin"). An operator still needs to SEE
        them, because "external connections are off for this tenant" is the
        answer to half the support tickets this page exists to triage — so they
        are shown, with no control to change them and a line saying who owns
        them. There is no sysadmin write path for them anywhere in the API.
      */}
      <SectionCard
        title="Tenant policy"
        icon={ShieldCheck}
        description="Owned by this tenant's own administrator (Admin › Settings › General). Shown here for support; not editable from the platform side."
      >
        {/*
          `!data.policySettings`, NOT `=== null` (review BF-04). An api bundle
          older than this web bundle — `deploy-ai1.sh web` landing before
          `deploy-ai1.sh api`, or a tab held open across the deploy on a cached
          detail response — omits the field entirely. `undefined !== null`, so
          the strict check fell through to the read branch and threw a
          TypeError, error-boundarying the whole Settings tab instead of showing
          the "could not be read" banner this branch exists to show.
        */}
        {!data.policySettings ? (
          <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2">
            <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <div>
              This tenant's policy settings could not be read. Until they can
              be, the tenant is denying web search and every outbound
              connection — this is an outage, not a configuration.
            </div>
          </div>
        ) : (
          <>
            <SettingField
              label="Allow web search"
              readOnly
              readOnlyReason="The tenant administrator owns this switch."
              description="Whether people in this tenant may turn on live web search per turn."
            >
              <Input
                value={data.policySettings.allowWebSearch ? 'On' : 'Off'}
                readOnly
                className="bg-muted/50"
              />
            </SettingField>
            <SettingField
              label="Allow external connections"
              readOnly
              readOnlyReason="The tenant administrator owns this switch."
              description="Whether this tenant may reach outside at all — connectors, MCP servers and web search. Off means fully inward-facing."
            >
              <Input
                value={data.policySettings.allowExternalConnections ? 'On' : 'Off'}
                readOnly
                className="bg-muted/50"
              />
            </SettingField>
          </>
        )}
      </SectionCard>

      {dirty && (
        <div className="sticky bottom-0 z-10 bg-background border-t pt-3 pb-3 flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={reset}
            disabled={update.isPending}
            title="Discard unsaved changes"
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
            Reset
          </Button>
          <Button
            size="sm"
            onClick={save}
            disabled={update.isPending || !!emailError}
          >
            <Save className="h-3.5 w-3.5 mr-1.5" />
            {update.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      )}
    </div>
  );
}
